import type { Rect } from "../scene/geometry";

/**
 * What the format of a video means for its layout (docs/05-video-und-untertitel.md
 * "Formate"): orientation, the areas platforms cover with their interface
 * (safe zones), the margins that keep small pictures and titles clear of
 * them, and where captions go by default. Everything is derived from the
 * frame size, so it works for every format, not only the presets.
 */

export interface FrameSize {
  width: number;
  height: number;
}

export type VideoOrientation = "portrait" | "square" | "landscape";

/** Portrait below 0.9 (width / height), landscape above 1.1, square in between. */
export function videoOrientation(frame: FrameSize): VideoOrientation {
  const aspect = frame.width / Math.max(1, frame.height);
  if (aspect < 0.9) return "portrait";
  if (aspect > 1.1) return "landscape";
  return "square";
}

/** 9:16-like formats (Reels, TikTok, Shorts), where the app covers the top and bottom. */
export function isTallFrame(frame: FrameSize): boolean {
  return frame.height >= frame.width * 1.6;
}

export interface SafeZones {
  /** Title of the toggle, e.g. "Reels, TikTok, Shorts". */
  title: string;
  /** Label on the canvas. */
  label: string;
  /** What the zones mean (tooltip). */
  description: string;
  /** Covered areas in frame px. */
  zones: Rect[];
}

/**
 * Areas of the frame that platforms cover or cut off, or null where nothing
 * covers the picture for good (landscape videos: the player controls only
 * show on hover or pause).
 *
 * - 9:16: profile, caption and buttons of Instagram Reels, TikTok and YouTube
 *   Shorts (measured on a 1080 × 1920 frame).
 * - 4:5 and square: Instagram's profile grid shows the middle 3:4 of posts.
 */
export function videoSafeZones(frame: FrameSize): SafeZones | null {
  const W = frame.width;
  const H = frame.height;
  if (isTallFrame(frame)) {
    const sx = W / 1080;
    const sy = H / 1920;
    return {
      title: "Reels, TikTok, Shorts",
      label: "Von der App verdeckt",
      description:
        "Profil, Beschreibung und Buttons von Instagram Reels, TikTok und YouTube Shorts liegen über diesen Bereichen.",
      zones: [
        { x: 0, y: 0, width: W, height: 220 * sy },
        { x: 0, y: H - 480 * sy, width: W, height: 480 * sy },
        { x: W - 160 * sx, y: 220 * sy, width: 160 * sx, height: H - 700 * sy },
      ],
    };
  }
  const aspect = W / Math.max(1, H);
  if (aspect > 0.76 && aspect <= 1.1) {
    const side = (W - H * 0.75) / 2;
    return {
      title: "Instagram-Profilraster (3:4)",
      label: "Im Profilraster abgeschnitten",
      description:
        "Im Profilraster zeigt Instagram nur die Mitte im Format 3:4 – im Feed ist das ganze Bild zu sehen.",
      zones: [
        { x: 0, y: 0, width: side, height: H },
        { x: W - side, y: 0, width: side, height: H },
      ],
    };
  }
  return null;
}

/** Why a format has no safe zones (for the disabled toggle). */
export const NO_SAFE_ZONES_HINT =
  "In diesem Format verdeckt keine App dauerhaft Teile des Bildes – Safe Zones gibt es für 9:16, 4:5 und 1:1.";

export interface FrameInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Margins for small pictures, titles and logos: below the status bar and
 * above the caption area of the apps in 9:16, otherwise a narrow margin.
 */
export function videoContentInsets(frame: FrameSize): FrameInsets {
  const margin = Math.round(Math.min(frame.width, frame.height) * 0.05);
  if (isTallFrame(frame)) {
    return {
      top: Math.max(margin, Math.round(frame.height * 0.12)),
      bottom: Math.max(margin, Math.round(frame.height * 0.26)),
      left: margin,
      right: margin,
    };
  }
  return { top: margin, bottom: margin, left: margin, right: margin };
}

/**
 * Default box of the captions: in 9:16 in the lower third (above the app's
 * caption area), in the other formats lower down, where viewers expect them.
 */
export function defaultCaptionBox(frame: FrameSize): Rect {
  const W = frame.width;
  const H = frame.height;
  if (isTallFrame(frame)) {
    const margin = Math.round(W * 0.07);
    const height = Math.round(H * 0.16);
    return { x: margin, y: Math.round(H * 0.66 - height / 2), width: W - margin * 2, height };
  }
  const short = Math.min(W, H);
  const margin = Math.round(short * 0.07);
  const height = Math.round(short * 0.2);
  const center =
    videoOrientation(frame) === "portrait"
      ? 0.78
      : videoOrientation(frame) === "square"
        ? 0.8
        : 0.82;
  return { x: margin, y: Math.round(H * center - height / 2), width: W - margin * 2, height };
}

/** Sizes of captions, titles and blocks refer to a frame whose shorter side is 1080 px. */
export function frameScale(frame: FrameSize): number {
  return Math.min(frame.width, frame.height) / 1080;
}

/**
 * Pixel size of an MP4 export: "1080p" / "720p" name the shorter side (a
 * 16:9 video in 1080p is 1920 × 1080, a reel 1080 × 1920). Never upscaled;
 * both sides even (needed for yuv420p).
 */
export function videoExportSize(
  frame: FrameSize,
  resolution: "1080p" | "720p",
): { width: number; height: number; scale: number } {
  const short = resolution === "720p" ? 720 : 1080;
  const scale = Math.min(1, short / Math.min(frame.width, frame.height));
  const even = (value: number) => Math.max(2, Math.round(value / 2) * 2);
  return { width: even(frame.width * scale), height: even(frame.height * scale), scale };
}
